import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ConnectorRow {
  readonly connectorId: string;
  readonly connectorCode: string;
  readonly connectorName: string;
  readonly connectorType: string;
  readonly vendor: string;
  readonly protocol: string;
  readonly direction: string;
  readonly status: string;
}

const TYPES = ['', 'ERP', 'CRM', 'HRMS', 'FINANCE', 'DOCUMENT', 'CLOUD_STORAGE', 'PAYMENT', 'SMS', 'EMAIL', 'WHATSAPP'];
const STATUSES = ['', 'DRAFT', 'ACTIVE', 'PAUSED', 'RETIRED'];
const CREATE_TYPES = TYPES.filter(Boolean);
const PROTOCOLS = ['REST', 'SOAP', 'GRAPHQL', 'FILE', 'DB', 'MESSAGE_QUEUE'];
const DIRECTIONS = ['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL'];

/** M29 — Connector Management (§33 / §10-§18). Lists pre-built and custom
 * integration connectors with their vendor, protocol, direction and runtime
 * status (BR-01: all external integrations flow through M29 connectors), and
 * operates the full connector lifecycle — create → activate → pause → retire,
 * plus manual execute and soft-delete — each wired to the guarded
 * /v1/eip/connectors write API. */
export function M29ConnectorManagementPage(): ReactNode {
  const { token } = useAuth();
  const { t: tr } = useTranslation();
  const [rows, setRows] = useState<ConnectorRow[]>([]);
  const [connectorType, setConnectorType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('ERP');
  const [vendor, setVendor] = useState('');
  const [protocol, setProtocol] = useState('REST');
  const [direction, setDirection] = useState('OUTBOUND');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (connectorType) params.set('connectorType', connectorType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ConnectorRow[] }>(`/v1/eip/connectors${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, connectorType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m29.connector.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m29.connector.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ConnectorRow }>(`/v1/eip/connectors`, {
        method: 'POST',
        body: {
          connectorName: name.trim(),
          connectorType: newType,
          vendor: vendor.trim() || undefined,
          protocol,
          direction,
        },
        token,
      });
      setNotice(tr('m29.connector.noticeCreated', { code: r.data.connectorCode }));
      setName('');
      setVendor('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m29.connector.verbCreate'), 'M29_CONNECTOR_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const activate = (c: ConnectorRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/connectors/${c.connectorId}/activate`, { method: 'POST', body: {}, token }),
      tr('m29.connector.noticeActivated', { code: c.connectorCode }), tr('m29.connector.verbActivate'), 'M29_CONNECTOR_MANAGE',
    );

  const pause = (c: ConnectorRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/connectors/${c.connectorId}/pause`, { method: 'POST', body: {}, token }),
      tr('m29.connector.noticePaused', { code: c.connectorCode }), tr('m29.connector.verbPause'), 'M29_CONNECTOR_MANAGE',
    );

  const retire = (c: ConnectorRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/connectors/${c.connectorId}/retire`, { method: 'POST', body: {}, token }),
      tr('m29.connector.noticeRetired', { code: c.connectorCode }), tr('m29.connector.verbRetire'), 'M29_CONNECTOR_MANAGE',
    );

  const execute = (c: ConnectorRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/connectors/${c.connectorId}/execute`, { method: 'POST', body: { configKey: 'DEFAULT' }, token }),
      tr('m29.connector.noticeExecuted', { code: c.connectorCode }), tr('m29.connector.verbExecute'), 'M29_CONNECTOR_EXECUTE',
    );

  const remove = (c: ConnectorRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/connectors/${c.connectorId}`, { method: 'DELETE', token }),
      tr('m29.connector.noticeDeleted', { code: c.connectorCode }), tr('m29.connector.verbDelete'), 'M29_CONNECTOR_MANAGE',
    );

  return (
    <main aria-label="connector-management">
      <h1>{tr('m29.connector.title')}</h1>

      <section aria-label="create-connector">
        <h2>{tr('m29.connector.newHeading')}</h2>
        <form aria-label="connector-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m29.connector.fieldName')}<input aria-label="field-connector-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m29.connector.fieldType')}
            <select aria-label="field-connector-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m29.connector.fieldVendor')}<input aria-label="field-vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} /></label>
          <label>{tr('m29.connector.fieldProtocol')}
            <select aria-label="field-protocol" value={protocol} onChange={(e) => setProtocol(e.target.value)}>
              {PROTOCOLS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label>{tr('m29.connector.fieldDirection')}
            <select aria-label="field-direction" value={direction} onChange={(e) => setDirection(e.target.value)}>
              {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-connector" disabled={busy}>{busy ? tr('m29.connector.creating') : tr('m29.connector.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="connector-error">{actionError}</p>}
        {notice && <p aria-label="connector-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m29.connector.filterType')}
          <select aria-label="filter-connector-type" value={connectorType} onChange={(e) => setConnectorType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m29.connector.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m29.connector.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m29.connector.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m29.connector.loading')}</p> : (
        <>
          <p aria-label="connector-count">{tr('m29.connector.count', { count: rows.length })}</p>
          <table aria-label="connector-table">
            <thead><tr><th>{tr('m29.connector.colCode')}</th><th>{tr('m29.connector.colName')}</th><th>{tr('m29.connector.colType')}</th><th>{tr('m29.connector.colVendor')}</th><th>{tr('m29.connector.colProtocol')}</th><th>{tr('m29.connector.colDirection')}</th><th>{tr('m29.connector.colStatus')}</th><th>{tr('m29.connector.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.connectorId}>
                  <td>{c.connectorCode}</td><td>{c.connectorName}</td><td>{c.connectorType}</td>
                  <td>{c.vendor}</td><td>{c.protocol}</td><td>{c.direction}</td><td>{c.status}</td>
                  <td>
                    <button type="button" aria-label={`activate-${c.connectorCode}`} onClick={() => void activate(c)}>{tr('m29.connector.btnActivate')}</button>
                    <button type="button" aria-label={`pause-${c.connectorCode}`} onClick={() => void pause(c)}>{tr('m29.connector.btnPause')}</button>
                    <button type="button" aria-label={`retire-${c.connectorCode}`} onClick={() => void retire(c)}>{tr('m29.connector.btnRetire')}</button>
                    <button type="button" aria-label={`execute-${c.connectorCode}`} onClick={() => void execute(c)}>{tr('m29.connector.btnExecute')}</button>
                    <button type="button" aria-label={`delete-${c.connectorCode}`} onClick={() => void remove(c)}>{tr('m29.connector.btnDelete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
